import { Link, useLocation, matchPath } from 'react-router-dom';
import IconAvatar from 'components/@extended/Avatar';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Badge, Box, Chip, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography, useMediaQuery } from '@mui/material';

// project import
import Dot from 'components/@extended/Dot';
import IconButton from 'components/@extended/IconButton';

import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// types
import { MenuOrientation, ThemeMode } from 'types/config';
import { LinkTarget, NavActionType, NavItemType } from 'types/menu';
import ProfileAvatar from '../../../../../sections/profiles/ProfileAvatar';
import React from 'react';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

export interface BadgeCounts {
  count: number;
  color?: string;
}

interface Props {
  item: NavItemType;
  level: number;
  isParents?: boolean;
}

const NavItem = ({ item, level, isParents = false }: Props) => {
  const theme = useTheme();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item?.link ? item.link : item.url!, end: false }, pathname);

  const { menuOrientation } = useConfig();
  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let itemIcon: any = false;
  const [primaryBadge, secondaryBadge] = item.badges || [];
  if (item.icon) {
    if (item.icon.anticon) {
      const Icon = item.icon.anticon;
      itemIcon = item.icon.anticon ? (
        <Icon
          style={{
            fontSize: drawerOpen ? '1rem' : '1.25rem',
            ...(menuOrientation === MenuOrientation.HORIZONTAL && isParents && { fontSize: 20, stroke: '1.5' })
          }}
        />
      ) : (
        false
      );
    } else {
      itemIcon = <IconAvatar color="secondary" src={item.icon} size={drawerOpen ? 'menuiconOpen' : 'menuiconClose'} />;
    }

    if (!!item.avatarForUser) {
      itemIcon = <ProfileAvatar user={item.avatarForUser} size={drawerOpen ? 'xs' : 'sm'} />;
    }
  }

  const textColor = theme.palette.mode === ThemeMode.DARK ? 'grey.400' : 'text.primary';
  const iconSelectedColor = theme.palette.mode === ThemeMode.DARK && drawerOpen ? 'text.primary' : 'primary.main';

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <Box sx={{ position: 'relative' }}>
          <ListItemButton
            component={Link}
            to={item.url!}
            target={itemTarget}
            disabled={item.disabled}
            selected={isSelected}
            sx={{
              zIndex: 1201,
              pl: drawerOpen ? `${level * 28}px` : 1.5,
              py: !drawerOpen && level === 1 ? 1.25 : 1,
              ...(drawerOpen && {
                '&:hover': {
                  bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'primary.lighter'
                },
                '&.Mui-selected': {
                  bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'primary.lighter',
                  borderRight: `2px solid ${theme.palette.primary.main}`,
                  color: iconSelectedColor,
                  '&:hover': {
                    color: iconSelectedColor,
                    bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'primary.lighter'
                  }
                }
              }),
              ...(!drawerOpen && {
                '&:hover': {
                  bgcolor: 'transparent'
                },
                '&.Mui-selected': {
                  '&:hover': {
                    bgcolor: 'transparent'
                  },
                  bgcolor: 'transparent'
                }
              })
            }}
            onClick={(e) => {
              if (isSelected) {
                e.preventDefault();
                handlerDrawerOpen(false);
              }
            }}
          >
            {itemIcon && (
              <Tooltip
                title={item.title}
                placement="right"
                arrow
                PopperProps={{
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      bgcolor: '#000000',
                      color: '#FFFFFF'
                    },
                    '& .MuiTooltip-arrow': {
                      color: 'black'
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <ListItemIcon
                      sx={{
                        minWidth: 28,
                        color: isSelected ? iconSelectedColor : textColor,
                        borderRadius: !drawerOpen ? 1.5 : undefined,
                        width: !drawerOpen ? 36 : undefined,
                        height: !drawerOpen ? 36 : undefined,
                        alignItems: !drawerOpen ? 'center' : undefined,
                        justifyContent: !drawerOpen ? 'center' : undefined,
                        position: 'relative',
                        lineHeight: 0,
                        '&:hover': {
                          bgcolor: !drawerOpen && theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.lighter'
                        },
                        ...(isSelected &&
                          !drawerOpen && {
                            bgcolor: theme.palette.mode === ThemeMode.DARK ? 'primary.900' : 'primary.lighter',
                            '&:hover': {
                              bgcolor: theme.palette.mode === ThemeMode.DARK ? 'primary.darker' : 'primary.lighter'
                            }
                          })
                      }}
                    >
                      {((primaryBadge?.count || 0) > 0 || (secondaryBadge?.count || 0) > 0) && !isSelected && !drawerOpen ? (
                          <Box sx={{
                            position: 'relative',
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {itemIcon}
                            {(primaryBadge?.count || 0) > 0 && (
                                <Badge
                                    badgeContent={primaryBadge!.count}
                                    sx={{
                                      '& .MuiBadge-badge': {
                                        backgroundColor: primaryBadge!.color,
                                        color: '#fff',
                                        right: (secondaryBadge?.count || 0) ? 15 : 0
                                      },
                                      position: 'absolute',
                                      top: 0,
                                      right: 0
                                    }}
                                />
                            )}
                            {(secondaryBadge?.count || 0) > 0 && (
                                <Badge
                                    badgeContent={secondaryBadge!.count}
                                    sx={{
                                      '& .MuiBadge-badge': {
                                        backgroundColor: secondaryBadge!.color,
                                        color: '#fff'
                                      },
                                      position: 'absolute',
                                      top: 0,
                                      zIndex: 1,
                                      right: (primaryBadge?.count || 0) === 0 ? 6 : -2.5
                                    }}
                                />
                            )}
                          </Box>
                      ) : (
                          itemIcon
                      )}
                    </ListItemIcon>
                    {((primaryBadge?.count || 0) > 0 || (secondaryBadge?.count || 0) > 0) && isSelected && !drawerOpen && (
                      <>
                        <Badge
                          badgeContent={primaryBadge?.count || 0}
                          sx={{
                            '& .MuiBadge-badge': {
                              backgroundColor: primaryBadge?.color,
                              color: '#fff',
                              right: secondaryBadge?.count ? 15 : 0
                            },
                            position: 'absolute',
                            right: 0
                          }}
                        />
                        {secondaryBadge && secondaryBadge?.count > 0 && (
                          <Badge
                            badgeContent={secondaryBadge.count}
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: secondaryBadge.color,
                                color: '#fff'
                              },
                              position: 'absolute',
                              zIndex: 1,
                              right: (primaryBadge?.count || 0) === 0 ? 6 : -2.5
                            }}
                          />
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </Tooltip>
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && (
              <ListItemText
                primary={
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Typography variant="h6" sx={{ color: isSelected ? iconSelectedColor : textColor }}>
                      {item.title}
                    </Typography>
                    {drawerOpen && ((primaryBadge?.count || 0) > 0 || (secondaryBadge?.count || 0) > 0) && (
                      <>
                        {(primaryBadge?.count || 0) > 0 && (
                          <Badge
                            badgeContent={primaryBadge.count}
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: primaryBadge.color,
                                color: '#fff'
                              },
                              position: 'absolute',
                              right: 0,
                              top: -6
                            }}
                          />
                        )}
                        {secondaryBadge && secondaryBadge.count > 0 && (
                          <Badge
                            badgeContent={secondaryBadge.count}
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: secondaryBadge.color,
                                color: '#fff'
                              },
                              position: 'absolute',
                              right: -17,
                              top: -6,
                              zIndex: 1
                            }}
                          />
                        )}
                      </>
                    )}
                  </Box>
                }
              />
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
              <Chip
                color={item.chip.color}
                variant={item.chip.variant}
                size={item.chip.size}
                label={item.chip.label}
                avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              />
            )}
          </ListItemButton>
          {(drawerOpen || (!drawerOpen && level !== 1)) &&
            item?.actions &&
            item?.actions.map((action, index) => {
              const ActionIcon = action.icon!;
              const callAction = action?.function;
              return (
                <IconButton
                  key={index}
                  {...(action.type === NavActionType.FUNCTION && {
                    onClick: (event) => {
                      event.stopPropagation();
                      callAction();
                    }
                  })}
                  {...(action.type === NavActionType.LINK && {
                    component: Link,
                    to: action.url,
                    target: action.target ? '_blank' : '_self'
                  })}
                  color="secondary"
                  variant="outlined"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 20,
                    zIndex: 1202,
                    width: 20,
                    height: 20,
                    mr: -1,
                    ml: 1,
                    color: 'secondary.dark',
                    borderColor: isSelected ? 'primary.light' : 'secondary.light',
                    '&:hover': { borderColor: isSelected ? 'primary.main' : 'secondary.main' }
                  }}
                >
                  <ActionIcon style={{ fontSize: '0.625rem' }} />
                </IconButton>
              );
            })}
        </Box>
      ) : (
        <ListItemButton
          component={Link}
          to={item.url!}
          target={itemTarget}
          disabled={item.disabled}
          selected={isSelected}
          sx={{
            zIndex: 1201,
            '&:hover': {
              bgcolor: 'transparent'
            },
            ...(isParents && {
              p: 1,
              mr: 1
            }),
            '&.Mui-selected': {
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'transparent'
              }
            }
          }}
        >
          {itemIcon && (
            <ListItemIcon
              sx={{
                minWidth: 28,
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  width: 28,
                  height: 28,
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    bgcolor: 'transparent'
                  }
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: 'transparent'
                    }
                  })
              }}
            >
              {itemIcon}
            </ListItemIcon>
          )}

          {!itemIcon && (
            <ListItemIcon
              sx={{
                color: isSelected ? 'primary.main' : 'secondary.dark',
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    bgcolor: 'transparent'
                  }
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: 'transparent'
                    }
                  })
              }}
            >
              <Dot size={4} color={isSelected ? 'primary' : 'secondary'} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={
              <Typography variant="h6" color={isSelected ? 'primary.main' : 'secondary.dark'}>
                {item.title}
              </Typography>
            }
          />
          {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
        </ListItemButton>
      )}
    </>
  );
};

export default NavItem;
